
import React from "react";
import { useOptimizedLeads } from "@/hooks/useOptimizedLeads";
import { LeadMetricsCards } from "./LeadMetricsCards";
import { LeadFunnelChart } from "./LeadFunnelChart";
import { LeadSourceAnalytics } from "./LeadSourceAnalytics";
import { LeadsTable } from "./LeadsTable";
import { LoadingState } from "@/components/error-handling/LoadingState";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertTriangle } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { useMemoryCleanup } from "@/utils/memory/memoryManager";

const LeadsDashboard: React.FC = () => {
  // Use optimized leads hook with pagination for memory efficiency
  const { data: leads = [], isLoading, error } = useOptimizedLeads({
    pageSize: 50 // Limit to 50 leads for dashboard view
  });
  
  // Memory cleanup for component
  useMemoryCleanup(() => {
    // Clean up any retained references
  });
  
  if (isLoading) {
    return <LoadingState message="Loading lead dashboard..." />;
  }
  
  if (error) {
    return (
      <Alert variant="destructive" className="mt-4">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Error Loading Leads</AlertTitle>
        <AlertDescription>{error.message || "An unknown error occurred."}</AlertDescription>
      </Alert>
    );
  }

  if (leads.length === 0) {
    return (
      <div className="text-center py-8">
        <h3 className="text-lg font-medium">No leads found</h3>
        <p className="text-muted-foreground mt-2">
          Start adding leads to see your dashboard metrics.
        </p>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* KPI Metrics */}
      <LeadMetricsCards leads={leads} />
      
      <Separator />
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Lead Funnel */}
        <div className="lg:col-span-2">
          <LeadFunnelChart leads={leads} />
        </div>
        
        {/* Source Analytics */}
        <div>
          <LeadSourceAnalytics leads={leads} />
        </div>
      </div>
      
      <Separator />
      
      {/* Leads Table */}
      <div>
        <h2 className="text-xl font-semibold mb-4">Leads Overview</h2>
        <LeadsTable leads={leads} />
      </div>
    </div>
  );
};

export default LeadsDashboard;
